import React from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getServicePackageName } from '../service-packages/servicePackageDisplay';
import CustomerServicePackageStatusChip from '../service-packages/CustomerServicePackageStatusChip';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const StoreCustomerServicePackageItem = ({
  customerPackage,
  canCancel,
  cancelling,
  onCancel,
  showStatus,
}) => {
  const { t, i18n } = useTranslation();
  const total = customerPackage.quantity || 0;
  const remaining = Math.max(0, customerPackage.remainingQuantity || 0);
  const progress = total > 0 ? Math.min(100, (remaining / total) * 100) : 0;
  const details = [
    customerPackage.expiresOn
      ? t('servicePackagesManagement.customer.expiresOn', { date: formatCalendarDate(customerPackage.expiresOn) })
      : t('servicePackagesManagement.customer.noExpiry'),
    customerPackage.paymentChannel
      ? t(`servicePackagesManagement.customer.channel.${customerPackage.paymentChannel}`, {
        defaultValue: customerPackage.paymentChannel,
      })
      : null,
    customerPackage.purchaseDate
      ? t('servicePackagesManagement.customer.purchasedOn', { date: formatCalendarDate(customerPackage.purchaseDate) })
      : null,
  ].filter(Boolean);

  return (
    <Box
      id={`customer-service-package-${customerPackage.id}`}
      sx={{
        py: 1.5,
        '&:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: BRAND.lineSoft,
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        gap={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.text, wordBreak: 'break-word' }}>
              {getServicePackageName(customerPackage.name, i18n.language, t)}
            </Typography>
            {showStatus ? (
              <CustomerServicePackageStatusChip effectiveStatus={customerPackage.effectiveStatus} />
            ) : null}
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.accentText, mb: 0.75 }}>
            {t('servicePackagesManagement.customer.remaining', { remaining, total })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            aria-label={t('servicePackagesManagement.customer.remaining', { remaining, total })}
            sx={{
              height: 6,
              borderRadius: 3,
              mb: 0.75,
              backgroundColor: brandAlpha('accent', 0.15),
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: BRAND.accent,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {details.join(' · ')}
          </Typography>
        </Box>
        {canCancel ? (
          <Button
            id={`customer-service-package-cancel-${customerPackage.id}`}
            size="small"
            color="error"
            onClick={() => onCancel(customerPackage)}
            disabled={cancelling}
            sx={{ textTransform: 'none', fontWeight: 600, alignSelf: { xs: 'flex-end', sm: 'flex-start' }, flexShrink: 0 }}
          >
            {t('servicePackagesManagement.customer.cancel.action')}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

export default StoreCustomerServicePackageItem;
